import type { Address } from "viem";
import { NUCLEUS_BASE_URL } from "../constants";
import type { ChainId } from "./vault-config";

export interface WithdrawAssetsResponse {
  [chainId: string]: {
    [vaultAddress: string]: {
      [tokenAddress: string]: number;
    };
  };
}

let assetsCache: WithdrawAssetsResponse | null = null;

/**
 * Interface for optional parameters when fetching withdraw assets
 */
interface FetchWithdrawAssetsParams {
  chainId?: ChainId;
  vaultAddress?: Address;
}

/**
 * Constructs the URL with optional query parameters
 */
function constructUrl(params?: FetchWithdrawAssetsParams): string {
  const withdrawAssetsUrl = `${NUCLEUS_BASE_URL}/withdraw-assets`;
  if (!params) return withdrawAssetsUrl;

  const queryParams = new URLSearchParams();
  if (params.chainId) queryParams.append("chainId", params.chainId.toString());
  if (params.vaultAddress)
    queryParams.append("vaultAddress", params.vaultAddress.toLowerCase());

  return `${withdrawAssetsUrl}?${queryParams.toString()}`;
}

/**
 * Fetches withdraw assets configuration from the API
 * @param params Optional parameters for chainId and vaultAddress
 * @returns Promise<WithdrawAssetsResponse>
 */
async function fetchWithdrawAssets(
  params?: FetchWithdrawAssetsParams
): Promise<WithdrawAssetsResponse> {
  // If no specific params are provided and we have cached data, return it
  if (!params && assetsCache) {
    return assetsCache;
  }

  try {
    const url = constructUrl(params);
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: WithdrawAssetsResponse = await response.json();

    // Only cache the full response (when no params are provided)
    if (!params) {
      assetsCache = data;
    }

    return data;
  } catch (error: unknown) {
    throw new Error(
      `An unknown error occurred while fetching withdraw assets: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
}

/**
 * Fetches withdraw assets for a specific chain
 * @param chainId The chain ID to fetch assets for
 * @returns Promise<WithdrawAssetsResponse[string]>
 */
async function fetchChainWithdrawAssets(
  chainId: ChainId
): Promise<WithdrawAssetsResponse[string]> {
  const assets = await fetchWithdrawAssets({ chainId });
  return assets[chainId.toString()];
}

/**
 * Fetches withdraw assets for a specific vault on a chain
 * @param chainId The chain ID
 * @param vaultAddress The vault address
 * @returns Promise<WithdrawAssetsResponse[string][string]>
 */
async function fetchVaultWithdrawAssets(
  chainId: ChainId,
  vaultAddress: Address
): Promise<WithdrawAssetsResponse[string][string]> {
  const assets = await fetchWithdrawAssets({ chainId, vaultAddress });
  return assets[chainId.toString()]?.[vaultAddress.toLowerCase()];
}

/**
 * Clears the assets cache
 */
function clearAssetsCache(): void {
  assetsCache = null;
}

export {
  clearAssetsCache,
  fetchChainWithdrawAssets,
  fetchVaultWithdrawAssets,
  fetchWithdrawAssets,
  type FetchWithdrawAssetsParams,
};
